import { ApiError, createApiClient, type ApiClient } from "@axisnode/api-client";
import { API_BASE, DDX_SYSTEM_ID, DDX_TENANT_ID } from "../config";

let singleton: ApiClient | null = null;

export function getDdxClient(): ApiClient {
  if (!singleton) {
    const headers: Record<string, string> = {};
    if (DDX_SYSTEM_ID) headers["X-System-Id"] = DDX_SYSTEM_ID;
    if (DDX_TENANT_ID) headers["X-Tenant-Id"] = DDX_TENANT_ID;
    singleton = createApiClient({
      baseUrl: API_BASE,
      defaultHeaders: headers,
    });
  }
  return singleton;
}

/** Test-only: replace the singleton. */
export function __setDdxClientForTests(client: ApiClient | null): void {
  singleton = client;
}

export type DdxHttpError = {
  status: number;
  body: unknown;
};

export function parseApiError(err: unknown): DdxHttpError | null {
  if (!(err instanceof ApiError)) return null;
  let body: unknown = err.body;
  try {
    body = JSON.parse(err.body);
  } catch {
    /* keep string */
  }
  // FastAPI often wraps as { detail: ... }
  if (
    body &&
    typeof body === "object" &&
    "detail" in (body as Record<string, unknown>)
  ) {
    body = (body as { detail: unknown }).detail;
  }
  return { status: err.status, body };
}

/**
 * Multipart upload — bypasses requestJson (JSON-body only, per api-client's
 * own TypedRequestOptions) via the client's exposed authFetch directly, no
 * Platform package change. FormData sets its own Content-Type boundary;
 * never set it manually or the multipart parse breaks server-side.
 *
 * authFetch is the RAW primitive — unlike requestJson/request, it does NOT
 * merge the client's defaultHeaders (X-System-Id/X-Tenant-Id are only
 * merged inside createApiClient's own `request()` wrapper). Without them,
 * every upload here was hitting _require_flag's fail-closed check with an
 * empty system id → a 404 from the FIRST line of upload_transcript, before
 * any file-type validation ever ran (found live: both .pdf and .txt
 * uploads 404'd identically). Attached explicitly here since this call
 * can't go through `request()` (FormData body, not JSON).
 */
export async function uploadTranscriptFile(
  projectId: string,
  file: File,
): Promise<{ asset_path: string; filename: string; size_bytes: number }> {
  const c = getDdxClient();
  const form = new FormData();
  form.append("file", file);
  const headers: Record<string, string> = {};
  if (DDX_SYSTEM_ID) headers["X-System-Id"] = DDX_SYSTEM_ID;
  if (DDX_TENANT_ID) headers["X-Tenant-Id"] = DDX_TENANT_ID;
  const res = await c.authFetch(`${c.baseUrl}/project/${projectId}/upload_transcript`, {
    method: "POST",
    headers,
    body: form,
  });
  const text = await res.text();
  if (!res.ok) throw new ApiError(res.status, text);
  return JSON.parse(text);
}

export { ApiError };
